import { boot } from 'quasar/wrappers'
import { useAuthStore, useMaintenanceStore } from '@/stores'
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { configureEcho } from '@laravel/echo-vue' // eslint-disable-line
import { getCsrfToken } from '@/helpers' // eslint-disable-line
import axios from 'axios'
import rateLimit from 'axios-rate-limit'
// import Pusher from 'pusher-js'

// window.Pusher = Pusher

// const getCookie = name => {
//   const cookie = {}
//   document.cookie.split(';').forEach(function (el) {
//     const [k, v] = el.split('=')
//     cookie[k.trim()] = v
//   })
//   return cookie[name]
// }

// Initialize HTTP client.
export const $axios = rateLimit(axios.create({
  baseURL: import.meta.env.API_HOST_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true,
  withXSRFToken: true
}), { maxRPS: 10 })

// Initialize query client.
export const $queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity
    }
  }
})
window.__TANSTACK_QUERY_CLIENT__ = $queryClient

let isLoggingOut = false

export default boot(async ({ app, store, router }) => {
  $axios.interceptors.response.use(
    response => response,
    async err => {
      const { refreshToken, refresh, logout } = useAuthStore()

      console.log('%cInterceptor response error', 'color:red', { err })

      // Blocked by maintenance mode — the body carries the full payload, so
      // the maintenance screen takes over without waiting for the next poll.
      if (err.response?.status === 503 && err.response.data?.message === 'maintenance.active') {
        useMaintenanceStore().setMaintenance(err.response.data.data)
        return Promise.reject(err)
      }

      if (err.response?.status === 401) {
        // A 401 from /oauth/token itself (e.g. an invalid_client the token
        // endpoint rejects — Passport/League returns 401 for this, not 400)
        // must NOT trigger another refresh() call here: refresh() posts to
        // this same endpoint, so re-entering it would await the very
        // refreshPromise this request's own failure needs to reject —
        // circular wait, deadlocked forever. Every future 401 anywhere in
        // the app would then hang on that same dead promise.
        const isTokenEndpoint = err.config?.url?.includes('/oauth/token')

        if (refreshToken && !isTokenEndpoint) {
          const refreshed = await refresh()
          if (refreshed) {
            // Re-read the store rather than reusing a destructured accessToken —
            // that would be the pre-refresh (already-invalid) snapshot taken
            // above, since destructuring a Pinia state value drops reactivity.
            // Retrying with it would 401 again and loop refresh forever.
            err.config.headers.Authorization = `Bearer ${useAuthStore().accessToken}`
            return $axios.request(err.config)
          }
          // refresh() already logs out on failure
        } else if (!isLoggingOut) {
          // Either there's no refresh token, or this 401 came from
          // /oauth/token itself — either way there's nothing left to try,
          // the session is dead, so redirect straight to login.
          isLoggingOut = true
          await logout()
          isLoggingOut = false
        }
      }

      return Promise.reject(err)
    }
  )

  // Initialize Vue Query.
  app.provide('$queryClient', $queryClient)
  app.use(VueQueryPlugin, { enableDevtoolsV6Plugin: true, queryClient: $queryClient })

  app.provide('$axios', $axios)
  store.use(() => ({ $axios }))
  store.use(() => ({ axios: $axios }))

  // console.log(getCsrfToken())
  // window.Laravel = {
  //   csrfToken: getCsrfToken()
  // }
  // console.log('%cReverb Port:', 'color:accent', import.meta.env.REVERB_PORT)
  // Initialize WebSocket.
  configureEcho({
    broadcaster: 'reverb',
    key: import.meta.env.REVERB_APP_KEY,
    wsHost: import.meta.env.REVERB_HOST,
    wssPort: import.meta.env.REVERB_PORT ?? 443,
    forceTLS: (import.meta.env.REVERB_SCHEME ?? 'https') === 'https',
    enabledTransports: ['ws', 'wss'],
    encrypted: true,
    authorizer: ({ name: channel_name }, options) => {
      return {
        authorize: async (socket_id, cb) => {
          try {
            // Always hits the api/auth:api-guarded route (routes/api.php), never
            // Laravel's own un-prefixed web-guarded broadcasting/auth default — that one
            // can't authenticate this SPA's bearer-token session.
            const pathname = `api/${import.meta.env.REVERB_AUTH_ENDPOINT}`
            const res = await $axios.post(pathname, { socket_id, channel_name })

            // console.log('%cSocket auth:', 'color:crimson', { channel_name, options, socket_id, res })
            cb(false, res.data)
          } catch (err) {
            cb(true, err)
          }
        }
      }
    }

  })

  // const $ws = new Echo({
  //   // Pusher,
  //   broadcaster: 'reverb',
  //   key: import.meta.env.REVERB_APP_KEY,
  //   wsHost: import.meta.env.REVERB_HOST,
  //   wsPort: import.meta.env.REVERB_PORT ?? 80,
  //   wssPort: import.meta.env.REVERB_PORT ?? 443,
  //   forceTLS: (import.meta.env.REVERB_SCHEME ?? 'https') === 'https',
  //   enabledTransports: ['ws', 'wss'],
  //   encrypted: true,
  //   // authEndpoint: `${import.meta.env.REVERB_AUTH_ENDPOINT}`
  //   // csrfToken: getCsrfToken(),
  //   // auth: {
  //   //   headers: {
  //   //     // Authorization: `Bearer ${useAuthStore().accessToken}`
  //   //     // 'X-CSRF-TOKEN': getCsrfToken()
  //   //   }
  //   // }
  //   // disableStats: true
  //   authorizer: ({ name: channel_name }, opts) => {
  //     // $axios.defaults.headers.Authorization = `Bearer ${useAuthStore().accessToken}`
  //     return {
  //       authorize: async (socket_id, cb) => {
  //         try {
  //           const res = await $axios.post(import.meta.env.REVERB_AUTH_ENDPOINT, { socket_id, channel_name })
  //           console.log('%cSocket auth:', 'color:crimson', { channel_name, opts, socket_id, res })
  //           cb(false, res.data)
  //         } catch (err) {
  //           cb(true, err)
  //         }
  //       }
  //     }
  //   }
  // })

  // store.use(() => ({ $ws }))
  // app.provide('$ws', $ws)
})
